/* ============================================================
 * src/components/primitives/index.tsx
 *
 * ESM transcription of the v.3 prototype's primitives.jsx. The
 * conversion is mechanical: the global `const { useState, ... } =
 * React` line is replaced with real imports, and the original file's
 * trailing `Object.assign(window, { ... })` is replaced with a single
 * named `export` so every screen can pull primitives by name.
 *
 * All visual code is preserved verbatim. `AccessGate`/`AdminGate`
 * still consult the mock store (`src/mock/circa.ts`) — wiring tasks
 * (T304+) will swap that import for the real `useCurrentMember()`
 * helper once it exists.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext,
  type ReactNode,
  type CSSProperties,
} from 'react';
// `CIRCA` (the prototype mock store) is no longer referenced from this
// module: AccessGate / AdminGate now consult `useCurrentMember()` and the
// client `PERMS` mirror at `src/lib/permissions.ts`. The mock store itself
// stays on disk until T350 audits the last screen off it.
import { useCurrentMember } from '../../hooks/useCurrentMember';
// Internal references to the WIRED primitives (T310). AdminGate composes
// EmptyState; the SkeletonStatRow / SkeletonCardGrid / SkeletonTable /
// SkeletonPanel / SkeletonList helpers compose Skeleton. The barrel
// re-exports both below so external callers get the same names.
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';

/* ---------------- Icons ---------------- */
const ICONS: Record<string, string> = {
  home: 'M3 10.5 12 3l9 7.5M5 9.5V21h5v-6h4v6h5V9.5',
  tv: 'M2 7h20v12H2zM7 3l5 4 5-4',
  radio: 'M4.9 19.1A10 10 0 0 1 4.9 4.9M7.8 16.2a6 6 0 0 1 0-8.4M16.2 7.8a6 6 0 0 1 0 8.4M19.1 4.9a10 10 0 0 1 0 14.2M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
  film: 'M3 4h18v16H3zM7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4',
  calendar: 'M3 5h18v16H3zM3 9h18M8 3v4M16 3v4',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 13a7.8 7.8 0 0 0 0-2l2-1.5-2-3.5-2.4 1a7.5 7.5 0 0 0-1.7-1l-.4-2.5h-4l-.4 2.5a7.5 7.5 0 0 0-1.7 1l-2.4-1-2 3.5L4.6 11a7.8 7.8 0 0 0 0 2l-2 1.5 2 3.5 2.4-1a7.5 7.5 0 0 0 1.7 1l.4 2.5h4l.4-2.5a7.5 7.5 0 0 0 1.7-1l2.4 1 2-3.5z',
  bell: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  menu: 'M3 12h18M3 6h18M3 18h18',
  chevronR: 'M9 18l6-6-6-6',
  chevronD: 'M6 9l6 6 6-6',
  chevronL: 'M15 18l-6-6 6-6',
  plus: 'M12 5v14M5 12h14',
  x: 'M18 6 6 18M6 6l12 12',
  check: 'M20 6 9 17l-5-5',
  checkCircle: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM9 12l2 2 4-4',
  alertCircle: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 8v4M12 16h.01',
  alertTriangle: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  uploadCloud: 'M16 16l-4-4-4 4M12 12v9M20.4 14.9A5 5 0 0 0 18 5.5h-1.3A8 8 0 1 0 3 13.3',
  play: 'M6 4l14 8-14 8z',
  pause: 'M6 4h4v16H6zM14 4h4v16h-4z',
  skip: 'M5 4l10 8-10 8zM19 5v14',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  moreH: 'M5 12h.01M12 12h.01M19 12h.01',
  moreV: 'M12 5h.01M12 12h.01M12 19h.01',
  eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  signal: 'M2 20h.01M7 20v-4M12 20v-8M17 20V8M22 20V4',
  gauge: 'M12 14l4-4M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z',
  loader: 'M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8',
  arrowUp: 'M12 19V5M5 12l7-7 7 7',
  arrowDown: 'M12 5v14M19 12l-7 7-7-7',
  arrowRight: 'M5 12h14M12 5l7 7-7 7',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6',
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  tag: 'M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 3 12V4h8a2 2 0 0 1 1.4.6l8.2 8.2a2 2 0 0 1 0 2.6ZM7 7h.01',
  globe: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20Z',
  zap: 'M13 2 3 14h9l-1 8 10-12h-9z',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
  layers: 'M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  grid: 'M3 3h8v8H3zM13 3h8v8h-8zM13 13h8v8h-8zM3 13h8v8H3z',
  filter: 'M22 3H2l8 9.5V19l4 2v-8.5z',
  refresh: 'M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  key: 'M14 7a4 4 0 1 1-5.7 5.7L3 18v3h3l1-1h2v-2h2l1.3-1.3A4 4 0 0 1 14 7ZM16.5 7.5h.01',
  linkIcon: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5',
  server: 'M3 4h18v6H3zM3 14h18v6H3zM7 7h.01M7 17h.01',
  database: 'M12 8c5 0 9-1.3 9-3s-4-3-9-3-9 1.3-9 3 4 3 9 3ZM3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5M3 12c0 1.7 4 3 9 3s9-1.3 9-3',
  fileText: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M9 13h6M9 17h6',
  message: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  sliders: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
  command: 'M18 3a3 3 0 0 0-3 3v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3Z',
  externalLink: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  sparkles: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM5 3v4M19 17v4M3 5h4M17 19h4',
  folder: 'M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z',
  mic: 'M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3ZM19 10v2a7 7 0 0 1-14 0v-2M12 19v3',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  bolt: 'M13 2 3 14h9l-1 8 10-12h-9z',
  cpu: 'M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2M4 4h16v16H4zM9 9h6v6H9z',
  power: 'M12 2v10M18.4 6.6a9 9 0 1 1-12.8 0',
};

export function Icon({ name, size = 16, color = 'currentColor', strokeWidth = 1.75, style, className }: { name: string; size?: number; color?: string; strokeWidth?: number; style?: CSSProperties; className?: string }) {
  const d = ICONS[name];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }} className={className} aria-hidden="true">
      {(d || '').split('M').filter(Boolean).map((seg, i) => <path key={i} d={'M' + seg} />)}
    </svg>
  );
}

/* ---------------- Btn ---------------- */
export function Btn({ children, variant = 'ghost', size = 'md', icon, iconRight, loading, disabled, onClick, as, href, full, tone, title, style, type = 'button' }: any) {
  const sizes: any = { sm: { p: '6px 12px', fs: 13, h: 30 }, md: { p: '9px 18px', fs: 14, h: 38 }, lg: { p: '12px 24px', fs: 15, h: 46 } };
  const sz = sizes[size];
  const base: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: sz.p, fontSize: sz.fs, fontWeight: size === 'md' || size === 'lg' ? 500 : 400,
    fontFamily: 'var(--font-sans)', borderRadius: 'var(--radius-pill)', cursor: disabled || loading ? 'not-allowed' : 'pointer',
    border: '1px solid transparent', transition: 'all .15s var(--ease-emphasized)', whiteSpace: 'nowrap',
    width: full ? '100%' : undefined, opacity: disabled ? 0.5 : 1, lineHeight: 1,
  };
  const variants: any = {
    solid: { background: 'var(--ink-muted)', color: '#fff', borderColor: 'var(--ink-muted)' },
    primary: { background: 'var(--cta)', color: '#fff', borderColor: 'var(--cta)' },
    ghost: { background: 'transparent', color: 'var(--ink-muted)', borderColor: 'var(--border-cool)' },
    text: { background: 'transparent', color: 'var(--cta)', borderColor: 'transparent', borderRadius: 'var(--radius-sm)', padding: '4px 2px' },
    danger: { background: 'transparent', color: 'var(--bad)', borderColor: 'var(--bad)' },
    onDark: { background: 'rgba(255,255,255,0.12)', color: '#fff', borderColor: 'rgba(255,255,255,0.25)' },
    onDarkSolid: { background: '#fff', color: 'var(--ink)', borderColor: '#fff' },
  };
  const Tag: any = as === 'a' ? 'a' : 'button';
  const [hover, setHover] = useState(false);
  const hoverStyle: CSSProperties = hover && !disabled && !loading ? (
    variant === 'solid' ? { background: '#000' } :
    variant === 'primary' ? { background: 'var(--cta-hover)' } :
    variant === 'ghost' ? { background: 'var(--lightest-gray)' } :
    variant === 'danger' ? { background: 'rgba(220,38,38,0.06)' } :
    variant === 'text' ? { color: 'var(--cta-hover)', textDecoration: 'underline' } :
    variant === 'onDark' ? { background: 'rgba(255,255,255,0.2)' } : {}
  ) : {};
  return (
    <Tag type={Tag === 'button' ? type : undefined} href={href} title={title}
      data-circa-action onClick={disabled || loading ? undefined : onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ ...base, ...variants[variant], ...hoverStyle, textDecoration: 'none', ...style }} disabled={disabled || loading}>
      {loading ? <Icon name="loader" size={14} style={{ animation: 'circa-spin 0.8s linear infinite' }} /> : (icon && <Icon name={icon} size={size === 'lg' ? 16 : 14} />)}
      {children && <span>{children}</span>}
      {!loading && iconRight && <Icon name={iconRight} size={14} />}
    </Tag>
  );
}

/* ---------------- Pill / Tag ---------------- */
export const TONES: Record<string, { bg: string; fg: string; dot: string }> = {
  live: { bg: 'rgba(220,38,38,0.08)', fg: '#DC2626', dot: '#DC2626' },
  good: { bg: 'rgba(22,163,74,0.10)', fg: '#15803d', dot: '#16A34A' },
  warn: { bg: 'rgba(245,158,11,0.12)', fg: '#b45309', dot: '#F59E0B' },
  bad: { bg: 'rgba(220,38,38,0.08)', fg: '#DC2626', dot: '#DC2626' },
  info: { bg: 'rgba(24,99,220,0.08)', fg: '#1863dc', dot: '#1863dc' },
  neutral: { bg: 'var(--lightest-gray)', fg: 'var(--slate)', dot: 'var(--muted-slate)' },
  green: { bg: 'var(--pale-green)', fg: 'var(--deep-green)', dot: 'var(--deep-green)' },
};

export function Pill({ children, tone = 'neutral', size = 'sm', dot, icon, style }: any) {
  const t = TONES[tone] || TONES.neutral;
  const pad = size === 'md' ? '4px 12px' : '4px 10px';
  const fs = size === 'md' ? 12 : 11;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: pad, fontSize: fs,
      fontWeight: 500, fontFamily: 'var(--font-sans)', borderRadius: 'var(--radius-pill)',
      background: t.bg, color: t.fg, whiteSpace: 'nowrap', lineHeight: 1.4, ...style }}>
      {(tone === 'live' || dot) && <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.dot,
        animation: tone === 'live' ? 'circa-pulse 1.4s infinite' : undefined }} />}
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  );
}

export function CircaTag({ children, onRemove }: any) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', fontSize: 12,
      fontFamily: 'var(--font-sans)', borderRadius: 'var(--radius-sm)', background: 'var(--lightest-gray)', color: 'var(--ink-muted)' }}>
      {children}
      {onRemove && <button onClick={onRemove} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--muted-slate)' }}><Icon name="x" size={12} /></button>}
    </span>
  );
}

/* ---------------- Panel ---------------- */
export function Panel({ children, title, right, pad = 24, radius = 22, style, noBorder, headStyle }: any) {
  return (
    <section style={{ background: 'var(--canvas)', border: noBorder ? 'none' : '1px solid var(--border-cool)',
      borderRadius: radius, overflow: 'hidden', ...style }}>
      {(title || right) && (
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: `${pad === 0 ? 20 : pad}px ${pad === 0 ? 20 : pad}px`, borderBottom: '1px solid var(--lightest-gray)', ...headStyle }}>
          {typeof title === 'string' ? <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500, letterSpacing: '-0.2px' }}>{title}</h3> : title}
          {right && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{right}</div>}
        </header>
      )}
      <div style={{ padding: pad }}>{children}</div>
    </section>
  );
}

/* ---------------- PageHead ---------------- */
export function PageHead({ title, description, actions, crumbs }: any) {
  return (
    <div style={{ marginBottom: 28 }}>
      {crumbs && <Breadcrumbs items={crumbs} />}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap', marginTop: crumbs ? 12 : 0 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 400, letterSpacing: '-0.8px', lineHeight: 1.05 }}>{title}</h1>
          {description && <p style={{ margin: '10px 0 0', fontSize: 16, color: 'var(--body-muted)', maxWidth: 620, lineHeight: 1.45 }}>{description}</p>}
        </div>
        {actions && <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>{actions}</div>}
      </div>
    </div>
  );
}

export function Breadcrumbs({ items }: { items: Array<{ label: string; href?: string }> }) {
  return (
    <nav style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--muted-slate)' }}>
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Icon name="chevronR" size={13} color="var(--muted-slate)" />}
          {it.href && i < items.length - 1
            ? <a href={it.href} style={{ color: 'var(--muted-slate)', textDecoration: 'none' }}>{it.label}</a>
            : <span style={{ color: i === items.length - 1 ? 'var(--ink-muted)' : 'var(--muted-slate)' }}>{it.label}</span>}
        </React.Fragment>
      ))}
    </nav>
  );
}

/* ---------------- Tabs ---------------- */
export function Tabs({ tabs, active, onChange, style }: any) {
  return (
    <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-cool)', ...style }}>
      {tabs.map((tab: any) => {
        const id = tab.id || tab;
        const label = tab.label || tab;
        const on = id === active;
        return (
          <button key={id} onClick={() => onChange(id)} style={{
            border: 'none', background: 'none', cursor: 'pointer', padding: '12px 14px', fontSize: 14,
            fontFamily: 'var(--font-sans)', fontWeight: on ? 500 : 400, color: on ? 'var(--ink)' : 'var(--muted-slate)',
            borderBottom: on ? '2px solid var(--ink)' : '2px solid transparent', marginBottom: -1, display: 'flex', alignItems: 'center', gap: 8 }}>
            {label}{tab.badge != null && <Pill tone={on ? 'green' : 'neutral'} style={{ padding: '1px 7px' }}>{tab.badge}</Pill>}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Spark ---------------- */
export function Spark({ data, w = 120, h = 32, color = 'var(--ink-muted)', fill }: any) {
  if (!data || !data.length) return null;
  const max = Math.max(...data), min = Math.min(...data);
  const rng = max - min || 1;
  const pts = data.map((d: number, i: number) => [(i / (data.length - 1)) * w, h - ((d - min) / rng) * (h - 4) - 2]);
  const line = pts.map((p: number[], i: number) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = line + ` L${w} ${h} L0 ${h} Z`;
  return (
    <svg width={w} height={h} style={{ display: 'block', overflow: 'visible' }}>
      {fill && <path d={area} fill={color} opacity={0.08} />}
      <path d={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------------- StatCard ---------------- */
export function StatCard({ label, value, delta, deltaTone, spark, sparkColor, hint, tone, icon, onClick, help }: any) {
  const arrow = delta == null ? null : delta > 0 ? 'arrowUp' : delta < 0 ? 'arrowDown' : 'arrowRight';
  const dt = deltaTone || (delta > 0 ? 'good' : delta < 0 ? 'bad' : 'neutral');
  return (
    <div onClick={onClick} className={onClick ? 'circa-lift' : undefined} style={{ background: 'var(--canvas)', border: '1px solid var(--border-cool)', borderRadius: 22,
      padding: 20, display: 'flex', flexDirection: 'column', gap: 10, cursor: onClick ? 'pointer' : 'default', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {icon && <Icon name={icon} size={13} color="var(--muted-slate)" />}{label}
          {help && <HelpDot text={help} />}
        </span>
        {delta != null && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 13, fontWeight: 500, color: TONES[dt].fg }}>
            <Icon name={arrow!} size={13} color={TONES[dt].fg} />{Math.abs(delta)}%
          </span>
        )}
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 400, letterSpacing: '-0.6px',
        color: tone ? TONES[tone].fg : 'var(--ink)' }}>{value}</div>
      {spark && <Spark data={spark} w={160} h={28} color={sparkColor || 'var(--muted-slate)'} fill />}
      {hint && <span style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{hint}</span>}
    </div>
  );
}

/* ---------------- ScoreRing ---------------- */
export function ScoreRing({ value, size = 96, label, stroke = 8 }: any) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  const col = value >= 90 ? 'var(--good)' : value >= 70 ? 'var(--warn)' : 'var(--bad)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--lightest-gray)" strokeWidth={stroke} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke}
            strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s var(--ease-emphasized)' }} />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-display)', fontSize: size * 0.28, fontWeight: 500 }}>{value}</div>
      </div>
      {label && <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{label}</span>}
    </div>
  );
}

/* ---------------- HelpDot ---------------- */
export function HelpDot({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} aria-label="Ayuda" style={{ border: 'none', background: 'var(--lightest-gray)', width: 16, height: 16,
        borderRadius: '50%', cursor: 'help', fontSize: 11, color: 'var(--muted-slate)', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', padding: 0, fontFamily: 'var(--font-sans)' }}>?</button>
      {open && (
        <span role="tooltip" style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
          width: 240, background: 'var(--ink)', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 12,
          lineHeight: 1.5, zIndex: 100, fontWeight: 400, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>{text}</span>
      )}
    </span>
  );
}

/* ---------------- TimerChip ---------------- */
export function TimerChip({ seconds, onDone, prefix }: { seconds: number; onDone?: () => void; prefix?: string }) {
  const [s, setS] = useState(seconds);
  useEffect(() => { setS(seconds); }, [seconds]);
  useEffect(() => {
    if (s <= 0) { onDone && onDone(); return; }
    const id = setTimeout(() => setS(s - 1), 1000);
    return () => clearTimeout(id);
  }, [s, onDone]);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return <span className="t-mono" style={{ fontSize: 12, color: 'var(--warn)', background: 'rgba(245,158,11,0.12)', padding: '2px 8px', borderRadius: 4 }}>{prefix}{mm}:{ss}</span>;
}

/* ---------------- AccessGate / AdminGate ----------------
 *
 * AccessGate is re-exported from the WIRED primitive at `./AccessGate.tsx`
 * (T310). The wired version consumes `useCurrentMember()` and consults
 * the client mirror at `src/lib/permissions.ts`, replacing the prototype's
 * synchronous mock-store check with the honest three-state contract
 * (resolving → Skeleton; allowed → children; insufficient → disable +
 * tooltip). See the AccessGate.tsx header for the full state diagram.
 *
 * AdminGate stays in this file as a thin wrapper around the wired
 * `useCurrentMember()` — its purpose (an admin-only screen guard) is
 * distinct from AccessGate (an admin-only control disabler) and the
 * design contract is "render a friendly 'Acceso restringido' panel,
 * never redirect" (CLAUDE.md §5).
 */
export { AccessGate } from './AccessGate';

export function AdminGate({ children }: { children: ReactNode }) {
  const m = useCurrentMember();
  if (m && m.role === 'admin') return <>{children}</>;
  // While resolving (m === undefined) OR signed-out (m === null) OR a
  // non-admin role: render the friendly restricted panel. Honest-state
  // contract: the operator sees the gate, not a flicker of admin content.
  return (
    <Panel><EmptyState icon="lock" title="Acceso restringido" body="Esta sección solo está disponible para administradores del espacio de trabajo." /></Panel>
  );
}

/* ---------------- CapPill ---------------- */
export function CapPill({ children }: { children?: ReactNode }) {
  return <Pill tone="warn" icon="info">{children || 'Datos aproximados'}</Pill>;
}

/* ---------------- FreshnessBanner ----------------
 *
 * Re-exported from the WIRED primitive at `./FreshnessBanner.tsx` (T310).
 * The wired version is typed to the closed 11-key `FreshnessKey` union
 * and consumes `useFreshness(cronKey)` directly — it renders nothing on
 * loading/fresh and renders the visible warning only on real staleness
 * (FR-007 + FR-016). The prototype's `{ message, onRefresh, show }` shape
 * is retired; callers must pass `cronKey` to bind a real freshness query.
 */
export { FreshnessBanner } from './FreshnessBanner';

/* ---------------- AuditTrailLink ---------------- */
export function AuditTrailLink({ onClick }: { onClick?: () => void }) {
  return <Btn size="sm" variant="text" icon="fileText" onClick={onClick}>Ver historial de auditoría</Btn>;
}

/* ---------------- Field / inputs ---------------- */
export function Field({ label, children, hint, error, required, htmlFor }: any) {
  return (
    <label htmlFor={htmlFor} style={{ display: 'block' }}>
      {label && <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink-muted)', marginBottom: 7 }}>{label}{required && <span style={{ color: 'var(--bad)' }}> *</span>}</span>}
      {children}
      {hint && !error && <span style={{ display: 'block', fontSize: 12, color: 'var(--muted-slate)', marginTop: 6 }}>{hint}</span>}
      {error && <span style={{ display: 'block', fontSize: 12, color: 'var(--bad)', marginTop: 6 }}>{error}</span>}
    </label>
  );
}

export function Input({ value, onChange, placeholder, type = 'text', prefix, icon, full = true, style, onKeyDown, autoFocus, disabled }: any) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${focus ? 'var(--focus-purple)' : 'var(--border-cool)'}`,
      borderRadius: 8, background: disabled ? 'var(--snow)' : 'var(--canvas)', overflow: 'hidden', width: full ? '100%' : undefined,
      boxShadow: focus ? '0 0 0 3px rgba(30,93,63,0.12)' : 'none', transition: 'all .12s', ...style }}>
      {icon && <span style={{ paddingLeft: 12, display: 'flex', color: 'var(--muted-slate)' }}><Icon name={icon} size={16} /></span>}
      {prefix && <span className="t-mono" style={{ paddingLeft: 12, fontSize: 12, color: 'var(--muted-slate)', whiteSpace: 'nowrap' }}>{prefix}</span>}
      <input value={value} onChange={e => onChange && onChange(e.target.value)} placeholder={placeholder} type={type}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} onKeyDown={onKeyDown} autoFocus={autoFocus} disabled={disabled}
        style={{ border: 'none', outline: 'none', padding: '10px 12px', fontSize: 14, flex: 1, background: 'transparent',
          fontFamily: 'var(--font-sans)', color: 'var(--ink-muted)', width: '100%', minWidth: 0 }} />
    </div>
  );
}

export function Textarea({ value, onChange, placeholder, rows = 4 }: any) {
  const [focus, setFocus] = useState(false);
  return (
    <textarea value={value} onChange={e => onChange && onChange(e.target.value)} placeholder={placeholder} rows={rows}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ width: '100%', border: `1px solid ${focus ? 'var(--focus-purple)' : 'var(--border-cool)'}`, borderRadius: 8,
        padding: '10px 12px', fontSize: 14, fontFamily: 'var(--font-sans)', resize: 'vertical', outline: 'none',
        boxShadow: focus ? '0 0 0 3px rgba(30,93,63,0.12)' : 'none', color: 'var(--ink-muted)' }} />
  );
}

export function Select({ value, onChange, options, full }: any) {
  return (
    <select value={value} onChange={e => onChange && onChange(e.target.value)}
      style={{ width: full ? '100%' : undefined, border: '1px solid var(--border-cool)', borderRadius: 8, padding: '10px 12px',
        fontSize: 14, fontFamily: 'var(--font-sans)', background: 'var(--canvas)', color: 'var(--ink-muted)', cursor: 'pointer', appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%2393939f' stroke-width='2'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: 34 }}>
      {options.map((o: any) => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
    </select>
  );
}

export function Toggle({ on, onChange, disabled }: any) {
  return (
    <button role="switch" aria-checked={on} disabled={disabled} onClick={() => !disabled && onChange && onChange(!on)} style={{
      width: 40, height: 23, borderRadius: 999, border: 'none', background: on ? 'var(--deep-green)' : 'var(--border-cool)',
      position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background .15s', padding: 0, opacity: disabled ? 0.5 : 1, flexShrink: 0 }}>
      <span style={{ position: 'absolute', top: 2, left: on ? 19 : 2, width: 19, height: 19, borderRadius: '50%', background: '#fff',
        transition: 'left .15s var(--ease-emphasized)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  );
}

export function SegmentedControl({ options, value, onChange, size = 'md' }: any) {
  return (
    <div style={{ display: 'inline-flex', background: 'var(--lightest-gray)', borderRadius: 8, padding: 3, gap: 2 }}>
      {options.map((o: any) => {
        const v = o.value || o, l = (o.label != null ? o.label : (typeof o === 'object' ? null : o)), on = v === value;
        return (
          <button key={v} onClick={() => onChange(v)} style={{ border: 'none', cursor: 'pointer', padding: size === 'sm' ? '5px 10px' : '7px 14px',
            fontSize: 13, fontWeight: on ? 500 : 400, fontFamily: 'var(--font-sans)', borderRadius: 6,
            background: on ? 'var(--ink-muted)' : 'transparent', color: on ? '#fff' : 'var(--slate)', display: 'flex', alignItems: 'center', gap: 6, transition: 'all .12s' }}>
            {o.icon && <Icon name={o.icon} size={14} />}{l}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Thumb ---------------- */
export function Thumb({ ratio = '16/9', label, color, radius = 22, live, style, children }: any) {
  return (
    <div style={{ aspectRatio: ratio, width: '100%', borderRadius: radius, position: 'relative', overflow: 'hidden',
      background: color ? `linear-gradient(135deg, ${color}, var(--forest-a))` : 'repeating-linear-gradient(135deg, var(--lightest-gray), var(--lightest-gray) 12px, #ececee 12px, #ececee 24px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
      {label && <span className="t-mono" style={{ fontSize: 11, color: color ? 'rgba(255,255,255,0.7)' : 'var(--muted-slate)' }}>{label}</span>}
      {live && <span style={{ position: 'absolute', top: 12, left: 12 }}><Pill tone="live">EN VIVO</Pill></span>}
      {children}
    </div>
  );
}

export function Avatar({ name, initials, size = 32, url }: any) {
  const ini = initials || (name || '?').split(' ').map((w: string) => w[0]).slice(0, 2).join('');
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--deep-green)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 600, flexShrink: 0,
      fontFamily: 'var(--font-sans)', backgroundImage: url ? `url(${url})` : undefined, backgroundSize: 'cover' }}>{!url && ini}</div>
  );
}

/* ---------------- EmptyState / Skeleton ----------------
 *
 * Re-exported from the WIRED primitives at `./EmptyState.tsx` and
 * `./Skeleton.tsx` (T310). Both files preserve the prototype's visual
 * contract verbatim — the `circa-skeleton` class on `Skeleton` is
 * load-bearing for the AccessGate test (it accepts either the class,
 * `aria-busy="true"`, or `role="progressbar"` as proof of the resolving
 * state). See the per-file headers for the test contract.
 */
export { EmptyState } from './EmptyState';
export { Skeleton } from './Skeleton';

/* ---------------- Loading system ---------------- */
export function useLoading(ms = 650, deps: ReadonlyArray<unknown> = []): boolean {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const id = setTimeout(() => setLoading(false), ms);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return loading;
}

export function SkeletonStatRow({ n = 4 }: { n?: number }) {
  return (
    <div className="circa-grid-4" style={{ marginBottom: 24 }}>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} style={{ background: 'var(--canvas)', border: '1px solid var(--border-cool)', borderRadius: 22, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Skeleton w="55%" h={11} r={4} />
          <Skeleton w="42%" h={26} r={6} />
          <Skeleton w="70%" h={10} r={4} />
        </div>
      ))}
    </div>
  );
}

export function SkeletonCardGrid({ n = 6, cols = 'circa-grid-3', media = 150 }: { n?: number; cols?: string; media?: number }) {
  return (
    <div className={cols}>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} style={{ border: '1px solid var(--border-cool)', borderRadius: 22, overflow: 'hidden', background: 'var(--canvas)' }}>
          <Skeleton w="100%" h={media} r={0} />
          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Skeleton w="65%" h={16} r={5} />
            <Skeleton w="90%" h={11} r={4} />
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}><Skeleton w={54} h={20} r={999} /><Skeleton w={46} h={20} r={999} /></div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <section style={{ background: 'var(--canvas)', border: '1px solid var(--border-cool)', borderRadius: 22, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 24, padding: '14px 20px', borderBottom: '1px solid var(--border-cool)' }}>
        {Array.from({ length: cols }).map((_, i) => <Skeleton key={i} w={i === 0 ? '32%' : '16%'} h={10} r={4} />)}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '16px 20px', borderBottom: r < rows - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
          {Array.from({ length: cols }).map((_, c) => <Skeleton key={c} w={c === 0 ? '32%' : '16%'} h={14} r={5} />)}
        </div>
      ))}
    </section>
  );
}

export function SkeletonPanel({ h = 220, title = true }: { h?: number; title?: boolean }) {
  return (
    <section style={{ background: 'var(--canvas)', border: '1px solid var(--border-cool)', borderRadius: 22, overflow: 'hidden' }}>
      {title && <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--lightest-gray)' }}><Skeleton w="36%" h={16} r={5} /></div>}
      <div style={{ padding: 20 }}><Skeleton w="100%" h={h} r={12} /></div>
    </section>
  );
}

export function SkeletonList({ rows = 5 }: { rows?: number }) {
  return (
    <section style={{ background: 'var(--canvas)', border: '1px solid var(--border-cool)', borderRadius: 22, overflow: 'hidden' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: i < rows - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
          <Skeleton w={40} h={40} r={10} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}><Skeleton w="40%" h={14} r={5} /><Skeleton w="60%" h={10} r={4} /></div>
          <Skeleton w={70} h={22} r={999} />
        </div>
      ))}
    </section>
  );
}

/* ---------------- Modal ---------------- */
export function Modal({ open, onClose, title, children, width = 560, footer }: any) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, animation: 'circa-fade .15s' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--canvas)', borderRadius: 22, width: '100%', maxWidth: width,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.24)', animation: 'circa-slide-up .2s var(--ease-emphasized)' }}>
        {title && (
          <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--lightest-gray)' }}>
            {typeof title === 'string' ? <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 400, letterSpacing: '-0.3px' }}>{title}</h2> : title}
            <button onClick={onClose} aria-label="Cerrar" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted-slate)', display: 'flex', padding: 4 }}><Icon name="x" size={20} /></button>
          </header>
        )}
        <div style={{ padding: 24, overflow: 'auto' }}>{children}</div>
        {footer && <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid var(--lightest-gray)' }}>{footer}</footer>}
      </div>
    </div>
  );
}

export function SlideOver({ open, onClose, title, children, width = 420, footer }: any) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, animation: 'circa-fade .15s' }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" style={{ position: 'absolute', top: 0, right: 0, height: '100%', width, maxWidth: '92vw',
        background: 'var(--canvas)', borderRadius: '22px 0 0 22px', borderLeft: '1px solid var(--border-cool)', display: 'flex',
        flexDirection: 'column', animation: 'circa-slide-in-right .22s var(--ease-emphasized)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid var(--lightest-gray)' }}>
          {typeof title === 'string' ? <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 400 }}>{title}</h2> : title}
          <button onClick={onClose} aria-label="Cerrar" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted-slate)', display: 'flex', padding: 4 }}><Icon name="x" size={20} /></button>
        </header>
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>{children}</div>
        {footer && <footer style={{ padding: '14px 20px', borderTop: '1px solid var(--lightest-gray)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>{footer}</footer>}
      </div>
    </div>
  );
}

/* ---------------- ConfirmModal ----------------
 *
 * Re-exported from the WIRED primitive at `./ConfirmModal.tsx` (T310).
 * The wired version enforces EXACT-match arming (no normalization),
 * renders `null` when closed (no leaked dialog content), and uses the
 * native `<button disabled>` so jsdom and real browsers both block a
 * click on the disarmed confirm. See ConfirmModal.tsx for the contract.
 */
export { ConfirmModal } from './ConfirmModal';

/* ---------------- Dropdown menu ---------------- */
export function Dropdown({ trigger, items, align = 'right' }: any) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <span onClick={() => setOpen(o => !o)}>{trigger}</span>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', [align]: 0, background: 'var(--canvas)', border: '1px solid var(--border-cool)',
          borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.14)', minWidth: 180, padding: 6, zIndex: 200, animation: 'circa-fade .12s' } as CSSProperties}>
          {items.map((it: any, i: number) => it.divider ? <div key={i} style={{ height: 1, background: 'var(--lightest-gray)', margin: '6px 0' }} /> : (
            <button key={i} onClick={() => { setOpen(false); it.onClick && it.onClick(); }} style={{ display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', border: 'none', background: 'none', cursor: 'pointer', padding: '9px 10px', borderRadius: 8, fontSize: 14,
              fontFamily: 'var(--font-sans)', color: it.danger ? 'var(--bad)' : 'var(--ink-muted)', textAlign: 'left' }}>
              {it.icon && <Icon name={it.icon} size={15} color={it.danger ? 'var(--bad)' : 'var(--muted-slate)'} />}{it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Progress ---------------- */
export function Progress({ value, h = 4, color = 'var(--cta)' }: { value: number; h?: number; color?: string }) {
  return <div style={{ height: h, background: 'var(--lightest-gray)', borderRadius: 999, overflow: 'hidden' }}>
    <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: 999, transition: 'width .4s var(--ease-emphasized)' }} /></div>;
}

/* ---------------- Toast context ---------------- */
const ToastCtx = createContext<{ toast: (opts: any) => void }>({ toast: () => {} });
export function useToast() { return useContext(ToastCtx); }

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<any[]>([]);
  const toast = useCallback((opts: any) => {
    const id = Math.random().toString(36).slice(2);
    const t = typeof opts === 'string' ? { message: opts, variant: 'info' } : opts;
    setToasts(ts => [...ts.slice(-2), { id, ...t }]);
    const ttl = t.variant === 'error' ? null : t.variant === 'success' ? 4000 : 6000;
    if (ttl) setTimeout(() => setToasts(ts => ts.filter(x => x.id !== id)), ttl);
  }, []);
  const dismiss = (id: string) => setToasts(ts => ts.filter(x => x.id !== id));
  const ICON: Record<string, string> = { success: 'checkCircle', error: 'alertCircle', info: 'info', warning: 'alertTriangle' };
  const COL: Record<string, string> = { success: 'var(--good)', error: 'var(--bad)', info: 'var(--cta)', warning: 'var(--warn)' };
  return (
    <ToastCtx.Provider value={{ toast }}>
      {children}
      <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
        {toasts.map(t => (
          <div key={t.id} role="status" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--canvas)',
            border: '1px solid var(--lightest-gray)', borderRadius: 22, padding: 14, maxWidth: 340, boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
            animation: 'circa-slide-up .2s var(--ease-emphasized)' }}>
            <Icon name={ICON[t.variant]} size={16} color={COL[t.variant]} style={{ marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, color: 'var(--ink-muted)' }}>{t.message}</div>
              {t.detail && <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginTop: 4, textTransform: 'none' }}>{t.detail}</div>}
            </div>
            {t.action && <button onClick={() => { t.action.onClick(); dismiss(t.id); }} style={{ border: 'none', background: 'none', color: 'var(--cta)', fontWeight: 500, cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-sans)' }}>{t.action.label}</button>}
            <button onClick={() => dismiss(t.id)} aria-label="Cerrar" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted-slate)', display: 'flex', padding: 0 }}><Icon name="x" size={14} /></button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useMutationWithToast() {
  const { toast } = useToast();
  return useCallback((fn: () => unknown, opts: { success?: string; error?: string } = {}) => {
    try { const r = fn && fn(); toast({ variant: 'success', message: opts.success || 'Cambios guardados.' }); return r; }
    catch (e: any) { toast({ variant: 'error', message: opts.error || 'No se pudo completar la acción.', detail: String(e && e.message || e) }); }
  }, [toast]);
}
